import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, AlertTriangle, Plus, Upload, Send, MessageSquare, FileText, Download, Lock, X } from "lucide-react";

const SEVERITY = { low: "Faible", medium: "Moyen", high: "Élevé", critical: "CRITIQUE" };
const STATUS = { open: "Ouvert", in_progress: "En cours", resolved: "Résolu", closed: "Clôturé" };
const RENT_STATUS_LABEL = { pending: "À échoir", paid: "Payé", late: "Retard", overdue: "EN DEMEURE" };
const RENT_STATUS_CLASS = { pending: "bg-slate-200", paid: "bg-emerald-200", late: "bg-yellow-200", overdue: "bg-red-200 font-bold" };
const INC_STATUS_CLASS = { open: "bg-yellow-100", in_progress: "bg-blue-100", resolved: "bg-emerald-100", closed: "bg-slate-200" };
const ATTACHMENT_ICON = { video: "🎬", image: "🖼" };
const fileKind = (file) => {
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("image/")) return "image";
  return "document";
};
const fmtMoney = (v) => v != null ? `${parseFloat(v).toLocaleString("fr-FR", {minimumFractionDigits:2})} €` : "—";
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—";

export default function TenantHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("rents");
  const [status, setStatus] = useState({ status: "active", overdue_count: 0, late_count: 0 });
  const [rents, setRents] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [text, setText] = useState("");
  const [newInc, setNewInc] = useState(null); // null or open form
  const [incForm, setIncForm] = useState({ type: "plomberie", severity: "medium", title: "", description: "", attachments: [] });
  const [signing, setSigning] = useState(null); // inventory

  useEffect(() => {
    if (user === false) navigate("/locataire/login");
    if (user && user.role !== "tenant") navigate("/");
  }, [user, navigate]);

  const reload = async () => {
    try {
      const [s, r, i, m, inv] = await Promise.all([
        api.get("/tenant/account-status"),
        api.get("/tenant/rents"),
        api.get("/tenant/incidents"),
        api.get("/tenant/messages"),
        api.get("/tenant/inventories"),
      ]);
      setStatus(s.data); setRents(r.data); setIncidents(i.data); setMessages(m.data); setInventories(inv.data);
    } catch (err) { console.error("TenantHub reload failed:", err); }
  };
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [s, r, i, m, inv] = await Promise.all([
          api.get("/tenant/account-status"),
          api.get("/tenant/rents"),
          api.get("/tenant/incidents"),
          api.get("/tenant/messages"),
          api.get("/tenant/inventories"),
        ]);
        if (cancelled) return;
        setStatus(s.data); setRents(r.data); setIncidents(i.data); setMessages(m.data); setInventories(inv.data);
      } catch (err) { console.error("TenantHub fetch failed:", err); }
    };
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const isBlocked = status.status === "blocked";

  const downloadReceipt = async (rentId, month) => {
    try {
      const r = await api.get(`/tenant/rents/${rentId}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `quittance-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Quittance indisponible"); }
  };

  const sendMsg = async () => {
    if (!text.trim()) return;
    if (isBlocked) { toast.error("Compte bloqué — régularisez votre situation."); return; }
    try {
      await api.post("/tenant/messages", { content: text });
      setText(""); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec"); }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/reservation-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const kind = fileKind(file);
      setIncForm(prev => ({ ...prev, attachments: [...prev.attachments, { ...data, kind }] }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec upload"); }
  };

  const submitIncident = async () => {
    if (isBlocked) { toast.error("Compte bloqué — régularisez votre situation pour signaler un incident."); return; }
    if (!incForm.title || !incForm.description) return;
    try {
      await api.post("/tenant/incidents", incForm);
      toast.success("Signalement envoyé — nous vous recontactons rapidement.");
      setNewInc(null); setIncForm({ type: "plomberie", severity: "medium", title: "", description: "", attachments: [] });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec"); }
  };

  const sigCanvasRef = useRef(null);
  const drawing = useRef(false);
  const drawStart = (e) => { drawing.current = true; const ctx = sigCanvasRef.current.getContext("2d"); ctx.beginPath(); const rect = sigCanvasRef.current.getBoundingClientRect(); const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left, y = (e.touches?.[0]?.clientY || e.clientY) - rect.top; ctx.moveTo(x, y); };
  const drawMove = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = sigCanvasRef.current.getContext("2d"); const rect = sigCanvasRef.current.getBoundingClientRect(); const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left, y = (e.touches?.[0]?.clientY || e.clientY) - rect.top; ctx.lineTo(x, y); ctx.strokeStyle = "#111"; ctx.lineWidth = 2; ctx.stroke(); };
  const drawEnd = () => { drawing.current = false; };
  const clearSig = () => { const c = sigCanvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const submitSig = async () => {
    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    try {
      await api.put(`/tenant/inventories/${signing.id}/sign`, { signature: dataUrl });
      toast.success("Signé électroniquement");
      setSigning(null); reload();
    } catch { toast.error("Échec signature"); }
  };

  const TABS = [
    { id: "rents", label: "Loyers & quittances", count: rents.length },
    { id: "incidents", label: "Incidents", count: incidents.length },
    { id: "inventories", label: "États des lieux", count: inventories.length },
    { id: "messages", label: "Messagerie", count: messages.length },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar /><Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/locataire")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Mon espace</button>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">Suivi & gestion</h1>

        {/* Banner — compte bloqué */}
        {isBlocked && (
          <div className="bg-red-100 border-2 border-red-600 p-6 mb-6 flex items-start gap-3" data-testid="banner-blocked">
            <Lock className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-bold text-red-900 text-lg mb-1">Compte suspendu — défaut de paiement</div>
              <p className="text-sm text-red-900">{status.overdue_count} loyer(s) en mise en demeure. La création de nouvelles demandes et l'envoi de messages sont suspendus jusqu'à régularisation. Contactez-nous au plus vite.</p>
            </div>
          </div>
        )}
        {!isBlocked && status.late_count > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-700" />
            <div className="text-sm">{status.late_count} loyer(s) en retard — merci de régulariser au plus vite.</div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`text-xs font-bold uppercase tracking-wider px-4 py-2.5 border border-slate-900 whitespace-nowrap ${tab === t.id ? "bg-slate-900 text-white" : "bg-white"}`}>
              {t.label} <span className="opacity-50">({t.count})</span>
            </button>
          ))}
        </div>

        {/* TAB - Rents */}
        {tab === "rents" && (
          <div className="bg-white border border-slate-900/15 overflow-x-auto" data-testid="tenant-rents-tab">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white"><tr><th className="px-3 py-3 text-left text-xs uppercase">Mois</th><th className="px-3 py-3 text-left text-xs uppercase">Montant</th><th className="px-3 py-3 text-left text-xs uppercase">Échéance</th><th className="px-3 py-3 text-left text-xs uppercase">Statut</th><th className="px-3 py-3 text-right text-xs uppercase">Quittance</th></tr></thead>
              <tbody>
                {rents.length === 0 && <tr><td colSpan="5" className="p-8 text-center opacity-50">Aucun loyer pour l'instant.</td></tr>}
                {rents.map(r => (
                  <tr key={r.id} className="border-t border-slate-900/10">
                    <td className="px-3 py-3 font-mono">{r.month}</td>
                    <td className="px-3 py-3"><b>{fmtMoney(r.amount_total)}</b><div className="text-xs opacity-50">{fmtMoney(r.amount_rent)}+{fmtMoney(r.amount_charges)} ch.</div></td>
                    <td className="px-3 py-3 text-xs font-mono">{r.due_date}</td>
                    <td className="px-3 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 ${RENT_STATUS_CLASS[r.status] || ""}`}>{RENT_STATUS_LABEL[r.status] || r.status}</span></td>
                    <td className="px-3 py-3 text-right">{r.status === "paid" && <button onClick={() => downloadReceipt(r.id, r.month)} data-testid={`dl-receipt-${r.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Download className="w-4 h-4"/></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TAB - Incidents */}
        {tab === "incidents" && (
          <div className="space-y-3" data-testid="tenant-incidents-tab">
            <button onClick={() => !isBlocked && setNewInc(true)} disabled={isBlocked} data-testid="new-incident-btn"
              className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
              <Plus className="w-4 h-4"/> Signaler un problème
            </button>
            {incidents.length === 0 ? (
              <div className="bg-white border border-slate-900/15 p-8 text-center opacity-60 text-sm">Aucun incident déclaré.</div>
            ) : incidents.map(i => (
              <div key={i.id} className="bg-white border border-slate-900/15 p-5" data-testid={`tenant-inc-${i.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-display font-bold text-base">{i.title}</h3>
                  <span className={`text-xs font-bold uppercase px-2 py-1 ${INC_STATUS_CLASS[i.status] || ""}`}>{STATUS[i.status]}</span>
                </div>
                <div className="text-xs opacity-60 mb-2">{i.type} · {SEVERITY[i.severity]} · {fmtDate(i.created_at)}</div>
                <p className="text-sm whitespace-pre-line">{i.description}</p>
                {i.attachments?.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{i.attachments.map((a, j) => <a key={`${i.id}-att-${a.url || j}`} href={a.url.startsWith("http") ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="text-xs border border-slate-900/15 px-2 py-1 hover:bg-[#FFC107]"><FileText className="w-3 h-3 inline mr-1"/>{a.name}</a>)}</div>}
              </div>
            ))}
          </div>
        )}

        {/* TAB - Inventories */}
        {tab === "inventories" && (
          <div className="space-y-3" data-testid="tenant-inv-tab">
            {inventories.length === 0 ? <div className="bg-white border border-slate-900/15 p-8 text-center opacity-60 text-sm">Aucun état des lieux pour l'instant.</div> :
              inventories.map(I => (
                <div key={I.id} className="bg-white border border-slate-900/15 p-5 flex items-center justify-between gap-3" data-testid={`tenant-inv-${I.id}`}>
                  <div>
                    <div className="font-display font-bold">État des lieux — {I.type === "entry" ? "Entrée" : "Sortie"}</div>
                    <div className="text-xs opacity-60">{fmtDate(I.created_at)} · {(I.rooms || []).length} pièce(s)</div>
                    <div className="flex gap-2 mt-2">
                      {I.tenant_signature && <span className="text-[10px] bg-emerald-100 px-2 py-0.5 text-emerald-900 font-bold">✓ Vous</span>}
                      {I.landlord_signature && <span className="text-[10px] bg-emerald-100 px-2 py-0.5 text-emerald-900 font-bold">✓ Bailleur</span>}
                      {I.state === "signed" && <span className="text-[10px] bg-[#FFC107] px-2 py-0.5 font-bold uppercase">Finalisé</span>}
                    </div>
                  </div>
                  {!I.tenant_signature && <button onClick={() => setSigning(I)} data-testid={`sign-inv-${I.id}`} className="btn-brutal btn-brutal-solar px-4 py-2 text-xs font-bold uppercase">Signer</button>}
                </div>
              ))
            }
          </div>
        )}

        {/* TAB - Messages */}
        {tab === "messages" && (
          <div className="bg-white border border-slate-900/15 flex flex-col" style={{ minHeight: 500 }} data-testid="tenant-msg-tab">
            <div className="flex-1 p-4 overflow-y-auto space-y-3">
              {messages.length === 0 && <div className="text-center opacity-50 text-sm mt-12 flex flex-col items-center gap-2"><MessageSquare className="w-8 h-8 opacity-30"/>Aucun message. Démarrez la conversation ci-dessous.</div>}
              {messages.map(m => (
                <div key={m.id} className={`max-w-[80%] ${m.from_role === "tenant" ? "ml-auto" : ""}`}>
                  <div className={`p-3 text-sm ${m.from_role === "tenant" ? "bg-[#FFC107]/30" : "bg-slate-900 text-white"}`}>{m.content}</div>
                  <div className="text-[10px] opacity-50 mt-1">{m.from_name} · {new Date(m.created_at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-900/10 flex gap-2">
              <input value={text} onChange={e => setText(e.target.value)} disabled={isBlocked} onKeyDown={e => e.key === "Enter" && sendMsg()} placeholder={isBlocked ? "Compte bloqué" : "Votre message..."} className="input flex-1 disabled:opacity-50" data-testid="tenant-msg-input" />
              <button onClick={sendMsg} disabled={isBlocked || !text.trim()} data-testid="tenant-msg-send" className="btn-brutal btn-brutal-solar px-4 py-2 text-sm font-bold uppercase flex items-center gap-1 disabled:opacity-50"><Send className="w-4 h-4"/></button>
            </div>
          </div>
        )}

        {/* New incident modal */}
        {newInc && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setNewInc(null)}>
            <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-900" onClick={e => e.stopPropagation()} data-testid="new-inc-modal">
              <div className="p-6 border-b flex items-center justify-between"><h2 className="font-display font-black text-xl">Signaler un problème</h2><button onClick={() => setNewInc(null)}><X className="w-5 h-5"/></button></div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Type">
                    <select value={incForm.type} onChange={e => setIncForm({...incForm, type: e.target.value})} className="input" data-testid="inc-type">
                      <option value="plomberie">Plomberie</option><option value="electricite">Électricité</option><option value="degat_eaux">Dégât des eaux</option><option value="sinistre">Sinistre</option><option value="securite">Sécurité</option><option value="autre">Autre</option>
                    </select>
                  </F>
                  <F label="Urgence">
                    <select value={incForm.severity} onChange={e => setIncForm({...incForm, severity: e.target.value})} className="input" data-testid="inc-severity">
                      <option value="low">Faible</option><option value="medium">Moyen</option><option value="high">Élevé</option><option value="critical">CRITIQUE</option>
                    </select>
                  </F>
                </div>
                <F label="Titre court *"><input value={incForm.title} onChange={e => setIncForm({...incForm, title: e.target.value})} className="input" data-testid="inc-title" /></F>
                <F label="Description détaillée *"><textarea rows={5} value={incForm.description} onChange={e => setIncForm({...incForm, description: e.target.value})} className="input resize-none" data-testid="inc-desc" /></F>
                <F label="Photos / vidéos">
                  <label className="block border-2 border-dashed border-slate-900/30 p-4 text-center cursor-pointer hover:border-slate-900">
                    <Upload className="w-5 h-5 mx-auto mb-1"/>
                    <span className="text-xs">Ajouter un fichier (image, vidéo, PDF, max 8 Mo)</span>
                    <input type="file" accept="image/*,video/*,application/pdf" onChange={e => uploadAttachment(e.target.files[0])} className="hidden" data-testid="inc-upload" />
                  </label>
                  {incForm.attachments.length > 0 && <div className="mt-2 space-y-1">{incForm.attachments.map((a, i) => <div key={`att-${a.url || a.name}-${i}`} className="text-xs bg-slate-50 p-2 flex justify-between"><span>{ATTACHMENT_ICON[a.kind] || "📄"} {a.name}</span><button onClick={() => setIncForm({...incForm, attachments: incForm.attachments.filter((_, idx) => idx !== i)})}>✕</button></div>)}</div>}
                </F>
              </div>
              <div className="p-6 border-t flex justify-end gap-3"><button onClick={() => setNewInc(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase">Annuler</button><button onClick={submitIncident} disabled={!incForm.title || !incForm.description} data-testid="inc-submit" className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase disabled:opacity-50">Envoyer</button></div>
            </div>
          </div>
        )}

        {/* Signature modal */}
        {signing && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-white max-w-lg w-full border border-slate-900" data-testid="sign-modal">
              <div className="p-6 border-b"><h2 className="font-display font-black text-xl">Signer électroniquement</h2><p className="text-sm opacity-60">{signing.type === "entry" ? "État d'entrée" : "État de sortie"}</p></div>
              <div className="p-6 space-y-3">
                <canvas ref={sigCanvasRef} width="450" height="180" className="border-2 border-slate-900 w-full touch-none bg-white"
                  onMouseDown={drawStart} onMouseMove={drawMove} onMouseUp={drawEnd} onMouseLeave={drawEnd}
                  onTouchStart={drawStart} onTouchMove={drawMove} onTouchEnd={drawEnd}
                  data-testid="sign-canvas" />
                <p className="text-xs opacity-60">Signez ci-dessus avec la souris ou votre doigt sur mobile.</p>
                <button onClick={clearSig} className="text-xs font-bold uppercase opacity-60 hover:opacity-100" data-testid="sign-clear">Effacer</button>
              </div>
              <div className="p-6 border-t flex justify-end gap-3"><button onClick={() => setSigning(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase">Annuler</button><button onClick={submitSig} data-testid="sign-submit" className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase">Confirmer la signature</button></div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
